import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import PaneHeader from "designSystem/components/pane/PaneHeader";
import { Button, FlexRowCenter } from "designSystem/index";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import * as topic from "dojo/topic";
import React, { useState } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { HeaderCallbacks, SelectedChannel } from "../../types";
import * as api from "../api";
import ChannelMenu from "./ChannelMenu";
import ConversationHistory from "./ConversationHistory";
import LoadingSlack from "./LoadingSlack";
import MessageEditor from "./MessageEditor";

const PREFIX = "DisplayChannel";

const classes = {
	root: `${PREFIX}root`,
	history: `${PREFIX}history`,
	message: `${PREFIX}message`,
};

const StyledGrid = styled(Grid)(({
	theme,
}) => ({
	[`&.${classes.root}`]: {
		height: "100%",
		width: "100%",
	},

	[`& .${classes.history}`]: {
		flex: 1,
		height: "100%",
		minHeight: 0,
		width: "100%",

	},

	[`& .${classes.message}`]: {
		borderTop: STANDARD_BORDER_STYLE,
		minHeight: "44px",
		width: "100%",
	},
}));

interface Props extends HeaderCallbacks {
	channel: SelectedChannel;

	/** type of node being connected to this channel */
	nodeType?: string;

	/** callback to when the user selects 'Join channel' */
	onJoinChannel?: (channelId: string) => void;

	/** callback to when the user selects 'Remove connection to channel' */
	onRemoveConnectionToChannel?: (channelId: string) => void;
}

const DisplayChannel: React.FunctionComponent<Props> = ({ channel, nodeType, onJoinChannel, onRemoveConnectionToChannel, onBack, onClose }) => {
	const [users, setUsers] = useState<api.UsersList>(null);
	const [history, setHistory] = useState<api.ConversationHistory>(null);
	const [lastUpdate, setLastUpdate] = useState<Date>(null);
	const [appendContext, setAppendContext] = useState<boolean>(true);
	const { showBoundary } = useErrorBoundary();

	function handleOnCheck(checked: boolean): void {
		setAppendContext(checked);
	}

	const fetchMessages = async () => {
		try {
			const messagesJson: api.ConversationHistory = await api.getChannelMessages(channel.channelId);
			setHistory(messagesJson);
			setLastUpdate(new Date());
		} catch (error) {
			showBoundary(error);
		}
	};

	const fetchUsersAndMessages = async () => {
		try {
			const usersJson: api.UsersList = await api.getUsers(channel.isPrivate ? channel.channelId : "");
			setUsers(usersJson);

			fetchMessages();
		} catch (error) {
			showBoundary(error);
		}
	};

	React.useEffect(() => {
		// fetch users and messages for the first time
		fetchUsersAndMessages();

		// subscribe to notifications from C8 server about the changed content of Slack channels
		const topicHandle = topic.subscribe("/csi/servermessage/SlackChannelChanged", (changedChannelId) => {
			if (changedChannelId == channel.channelId) {
				fetchMessages();
			}
		});

		return () => {
			// remove topic subscription when the wiget is destroyed
			topicHandle.remove();
		};
	}, []);

	async function handleSend(userMessage: string) {
		try {
			const contextUrl = appendContext ? location.href : "";
			await api.postMessageToChannel(channel.channelId, userMessage, "draftjs", contextUrl);
			fetchMessages();
		} catch (error) {
			showBoundary(error);
		}
	}

	return (
		<StyledGrid
			className={classes.root}
			container
			direction="column"
		>
			<PaneHeader
				back
				onBack={onBack}
				onClose={onClose}
				title={channel.channelName}
			>
				<ChannelMenu
					channelId={channel.channelId}
					ml={2}
					nodeType={nodeType}
					onRemoveConnectionToChannel={onRemoveConnectionToChannel}
				/>
			</PaneHeader>
			{
				null == lastUpdate
					? <LoadingSlack />
					: (
						<React.Fragment>
							<Grid className={classes.history} item>
								<ConversationHistory
									channelId={channel.channelId}
									onRefresh={fetchUsersAndMessages}
									{...{ users, history, lastUpdate }}
								/>
							</Grid>
							{
								channel.isMember
									? (
										<Grid className={classes.message} item>
											<MessageEditor
												onCheckAppendCallback={handleOnCheck}
												onSend={handleSend}
												users={users}
											/>
										</Grid>
									)
									: (
										<FlexRowCenter
											borderTop={STANDARD_BORDER_STYLE}
											justifyContent="center"
											p={2}
										>
											<Button
												design="primary"
												fullWidth
												onClick={() => onJoinChannel?.(channel.channelId)}
												res="JoinChannel"
											/>
										</FlexRowCenter>
									)
							}
						</React.Fragment>
					)
			}
		</StyledGrid>
	);
};

export default DisplayChannel;

